import { useContext } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AuthContext } from '@@/contexts/AuthContext';
import { ResultValue } from '@@/types/ApiResult';
import { DfcApiClient } from '../api/dfc-api-client';
import type { ItemRecord, DeleteItemRequest, ItemReference } from '../api/api-items';
import type { CreateItemPayload, UpdateItemPayload } from '../schemas/item';

const ITEMS_QUERY_KEY = 'dfc-items';
const ITEM_REFERENCES_QUERY_KEY = 'dfc-item-references';

export const itemsQueryKeys = {
  base: (params: { instanceId: string; region: string }) =>
    [ITEMS_QUERY_KEY, params.instanceId, params.region] as const,

  list: (params: { instanceId: string; region: string; entityId: string }) =>
    [...itemsQueryKeys.base(params), 'list', params.entityId] as const,

  detail: (params: { instanceId: string; region: string; entityId: string; itemId: string }) =>
    [...itemsQueryKeys.base(params), 'detail', params.entityId, params.itemId] as const,

  references: (params: { instanceId: string; region: string; entityId: string; itemId: string }) =>
    [ITEM_REFERENCES_QUERY_KEY, params.instanceId, params.region, params.entityId, params.itemId] as const
};

export const useListItems = (entityId: string, options?: { enabled?: boolean }) => {
  const { instanceData } = useContext(AuthContext);

  const fetchItems = async (): Promise<ItemRecord[]> => {
    if (!instanceData?.instanceId || !entityId) {
      throw new Error('Missing required context data or entity');
    }

    const dfcApiClient = new DfcApiClient();
    const result = await dfcApiClient.items.listItems({
      input: { entityId: entityId },
      queryString: {
        instanceId: instanceData.instanceId,
        region: instanceData.region
      }
    });

    if (!ResultValue.ok(result)) {
      throw new Error(result.message || 'Failed to fetch items');
    }

    return result.data.items;
  };

  return useQuery({
    queryKey: itemsQueryKeys.list({
      instanceId: instanceData?.instanceId ?? '',
      region: instanceData?.region ?? '',
      entityId
    }),
    queryFn: fetchItems,
    enabled: !!(instanceData?.instanceId && entityId && options?.enabled !== false)
  });
};

export const useGetItem = (entityId: string, itemId: string, options?: { enabled?: boolean }) => {
  const { instanceData } = useContext(AuthContext);

  const fetchItem = async (): Promise<ItemRecord> => {
    if (!instanceData?.instanceId || !entityId || !itemId) {
      throw new Error('Missing required context data, entity, or item');
    }

    const dfcApiClient = new DfcApiClient();
    const result = await dfcApiClient.items.getItem({
      input: {
        entityId: entityId,
        itemId: itemId
      },
      queryString: {
        instanceId: instanceData.instanceId,
        region: instanceData.region
      }
    });

    if (!ResultValue.ok(result)) {
      throw new Error(result.message || 'Failed to fetch item');
    }

    return result.data.item;
  };

  return useQuery({
    queryKey: itemsQueryKeys.detail({
      instanceId: instanceData?.instanceId ?? '',
      region: instanceData?.region ?? '',
      entityId,
      itemId
    }),
    queryFn: fetchItem,
    enabled: !!(instanceData?.instanceId && entityId && itemId && options?.enabled !== false)
  });
};

export const useCreateItem = () => {
  const { instanceData } = useContext(AuthContext);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: { entityId: string; data: CreateItemPayload }): Promise<ItemRecord> => {
      if (!instanceData?.instanceId) {
        throw new Error('Missing required context data');
      }

      const dfcApiClient = new DfcApiClient();
      const result = await dfcApiClient.items.createItem({
        entityId: params.entityId,
        input: params.data,
        queryString: {
          instanceId: instanceData.instanceId,
          region: instanceData.region
        }
      });

      if (!ResultValue.ok(result)) {
        throw new Error(result.message);
      }

      return result.data.item;
    },
    onSuccess: (_, variables) => {
      if (!instanceData?.instanceId || !instanceData?.region) return;
      queryClient.invalidateQueries({
        queryKey: itemsQueryKeys.list({
          instanceId: instanceData.instanceId,
          region: instanceData.region,
          entityId: variables.entityId
        })
      });
    }
  });
};

export const useUpdateItem = () => {
  const { instanceData } = useContext(AuthContext);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: {
      entity: string;
      path: string;
      data: UpdateItemPayload;
    }): Promise<{ path: string; entity: string }> => {
      if (!instanceData?.instanceId) {
        throw new Error('Missing required context data');
      }

      const dfcApiClient = new DfcApiClient();
      const result = await dfcApiClient.items.updateItem({
        entity: params.entity,
        path: params.path,
        input: params.data,
        queryString: {
          instanceId: instanceData.instanceId,
          region: instanceData.region
        }
      });

      if (!ResultValue.ok(result)) {
        throw new Error(result.message);
      }

      return result.data.item;
    },
    onSuccess: (_, variables) => {
      if (!instanceData?.instanceId || !instanceData?.region) return;
      queryClient.invalidateQueries({
        queryKey: itemsQueryKeys.list({
          instanceId: instanceData.instanceId,
          region: instanceData.region,
          entityId: variables.entity
        })
      });
      queryClient.invalidateQueries({
        queryKey: itemsQueryKeys.detail({
          instanceId: instanceData.instanceId,
          region: instanceData.region,
          entityId: variables.entity,
          itemId: variables.path
        })
      });
    }
  });
};

export const useDeleteItem = () => {
  const { instanceData } = useContext(AuthContext);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: DeleteItemRequest): Promise<void> => {
      if (!instanceData?.instanceId) {
        throw new Error('Missing required context data');
      }

      const dfcApiClient = new DfcApiClient();
      const result = await dfcApiClient.items.deleteItem({
        input: params,
        queryString: {
          instanceId: instanceData.instanceId,
          region: instanceData.region
        }
      });

      if (!ResultValue.ok(result)) {
        throw new Error(result.message);
      }
    },
    onSuccess: (_, variables) => {
      if (!instanceData?.instanceId || !instanceData?.region) return;
      queryClient.invalidateQueries({
        queryKey: itemsQueryKeys.list({
          instanceId: instanceData.instanceId,
          region: instanceData.region,
          entityId: variables.entityId
        })
      });
    }
  });
};

export const useGetItemReferences = (entityId: string, itemId: string, options?: { enabled?: boolean }) => {
  const { instanceData } = useContext(AuthContext);

  const fetchReferences = async (): Promise<{
    referencedBy: ItemReference[];
    referencesTo: ItemReference[];
  }> => {
    if (!instanceData?.instanceId || !entityId || !itemId) {
      throw new Error('Missing required context data, entity, or item');
    }

    const dfcApiClient = new DfcApiClient();
    const result = await dfcApiClient.items.getItemReferences({
      input: { entityId, itemId },
      queryString: {
        instanceId: instanceData.instanceId,
        region: instanceData.region
      }
    });

    if (!ResultValue.ok(result)) {
      throw new Error(result.message || 'Failed to fetch item references');
    }

    return {
      referencedBy: result.data.referencedBy,
      referencesTo: result.data.referencesTo
    };
  };

  return useQuery({
    queryKey: itemsQueryKeys.references({
      instanceId: instanceData?.instanceId ?? '',
      region: instanceData?.region ?? '',
      entityId,
      itemId
    }),
    queryFn: fetchReferences,
    enabled: !!(instanceData?.instanceId && entityId && itemId && options?.enabled !== false),
    staleTime: 0
  });
};
